import * as THREE from 'three';
import { TextSprite } from '../text-sprite.js';

export class Volume extends THREE.Object3D {
  _clip: boolean;
  _visible = true;
  showVolumeLabel = true;
  _modifiable: boolean;
  label: TextSprite;

  constructor(args: { clip?: boolean; modifiable?: boolean } = {}) {
    super();

    if (new.target === Volume) {
      console.warn(
        "Can't create object of class Volume directly. Use classes BoxVolume or SphereVolume instead.",
      );
    }

    this._clip = args.clip || false;
    this._modifiable = args.modifiable ?? true;

    this.label = new TextSprite('0');
    this.label.setBorderColor({ r: 0, g: 255, b: 0, a: 0.0 });
    this.label.setBackgroundColor({ r: 0, g: 255, b: 0, a: 0.0 });
    this.label.material.depthTest = false;
    this.label.material.depthWrite = false;
    this.label.material.transparent = true;
    this.label.position.y -= 0.5;
    this.add(this.label);

    this.label.updateMatrixWorld = () => {
      const volumeWorldPos = new THREE.Vector3();
      volumeWorldPos.setFromMatrixPosition(this.matrixWorld);
      this.label.position.copy(volumeWorldPos);
      this.label.updateMatrix();
      this.label.matrixWorld.copy(this.label.matrix);
      this.label.matrixWorldNeedsUpdate = false;

      for (let i = 0, l = this.label.children.length; i < l; i++) {
        this.label.children[i].updateMatrixWorld(true);
      }
    };

    {
      (this as any).addEventListener('select', () => {});
      (this as any).addEventListener('deselect', () => {});
    }

    Object.defineProperty(this, 'visible', {
      get: () => this._visible,
      set: (value: boolean) => {
        if (this._visible !== value) {
          this._visible = value;
          this.dispatchEvent({ type: 'visibility_changed', object: this } as any);
        }
      },
      configurable: true,
      enumerable: true,
    });
  }

  getVolume(): number {
    console.warn('override this in subclass');
    return 0;
  }

  update(): void {}

  raycast(_raycaster: THREE.Raycaster, _intersects: THREE.Intersection[]): void {}

  get clip(): boolean {
    return this._clip;
  }

  set clip(value: boolean) {
    if (this._clip !== value) {
      this._clip = value;
      this.update();
      this.dispatchEvent({ type: 'clip_changed', object: this } as any);
    }
  }

  get modifieable(): boolean {
    return this._modifiable;
  }

  set modifieable(value: boolean) {
    this._modifiable = value;
    this.update();
  }
}

export class BoxVolume extends Volume {
  static counter = 0;

  material: THREE.MeshBasicMaterial;
  box: THREE.Mesh;
  boundingBox: THREE.Box3;
  boundingSphere?: THREE.Sphere;
  frame: THREE.LineSegments;

  constructor(args: { clip?: boolean; modifiable?: boolean } = {}) {
    super(args);

    BoxVolume.counter++;
    this.name = 'box_' + BoxVolume.counter;

    const boxGeometry = new THREE.BoxGeometry(1, 1, 1);
    boxGeometry.computeBoundingBox();

    const boxFrameGeometry = new THREE.BufferGeometry();
    {
      const positions = new Float32Array([
        // bottom
        -0.5, -0.5, 0.5, 0.5, -0.5, 0.5, 0.5, -0.5, 0.5, 0.5, -0.5, -0.5, 0.5, -0.5, -0.5, -0.5,
        -0.5, -0.5, -0.5, -0.5, -0.5, -0.5, -0.5, 0.5,
        // top
        -0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, -0.5, 0.5, 0.5, -0.5, -0.5, 0.5,
        -0.5, -0.5, 0.5, -0.5, -0.5, 0.5, 0.5,
        // sides
        -0.5, -0.5, 0.5, -0.5, 0.5, 0.5, 0.5, -0.5, 0.5, 0.5, 0.5, 0.5, 0.5, -0.5, -0.5, 0.5, 0.5,
        -0.5, -0.5, -0.5, -0.5, -0.5, 0.5, -0.5,
      ]);
      boxFrameGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    }

    this.material = new THREE.MeshBasicMaterial({
      color: 0x00ff00,
      transparent: true,
      opacity: 0.3,
      depthTest: true,
      depthWrite: false,
    });
    this.box = new THREE.Mesh(boxGeometry, this.material);
    this.box.geometry.computeBoundingBox();
    this.boundingBox = this.box.geometry.boundingBox!;
    this.add(this.box);

    this.frame = new THREE.LineSegments(
      boxFrameGeometry,
      new THREE.LineBasicMaterial({ color: 0x000000 }),
    );
    this.add(this.frame);

    this.update();
  }

  update(): void {
    this.boundingBox = this.box.geometry.boundingBox!;
    this.boundingSphere = this.boundingBox.getBoundingSphere(new THREE.Sphere());

    if (this._clip) {
      this.box.visible = false;
      this.label.visible = false;
    } else {
      this.box.visible = true;
      this.label.visible = this.showVolumeLabel;
    }
  }

  raycast(raycaster: THREE.Raycaster, intersects: THREE.Intersection[]): void {
    const is: THREE.Intersection[] = [];
    this.box.raycast(raycaster, is);

    if (is.length > 0) {
      const I = is[0];
      intersects.push({
        distance: I.distance,
        object: this,
        point: I.point.clone(),
      } as any);
    }
  }

  getVolume(): number {
    return Math.abs(this.scale.x * this.scale.y * this.scale.z);
  }
}

export class SphereVolume extends Volume {
  static counter = 0;

  material: THREE.MeshBasicMaterial;
  sphere: THREE.Mesh;
  boundingBox: THREE.Box3;
  boundingSphere?: THREE.Sphere;
  frame: THREE.LineSegments | THREE.Mesh;

  constructor(args: { clip?: boolean; modifiable?: boolean } = {}) {
    super(args);

    SphereVolume.counter++;
    this.name = 'sphere_' + SphereVolume.counter;

    const sphereGeometry = new THREE.SphereGeometry(1, 32, 32);
    sphereGeometry.computeBoundingBox();

    this.material = new THREE.MeshBasicMaterial({
      color: 0x00ff00,
      transparent: true,
      opacity: 0.3,
      depthTest: true,
      depthWrite: false,
    });
    this.sphere = new THREE.Mesh(sphereGeometry, this.material);
    this.sphere.visible = false;
    this.sphere.geometry.computeBoundingBox();
    this.boundingBox = this.sphere.geometry.boundingBox!;
    this.add(this.sphere);

    this.label.visible = false;

    const frameGeometry = new THREE.BufferGeometry();
    {
      const steps = 64;
      const uSegments = 8;
      const vSegments = 5;
      const positions: number[] = [];

      for (let uSegment = 0; uSegment < uSegments; uSegment++) {
        const alpha = (uSegment / uSegments) * Math.PI * 2;
        const dirx = Math.cos(alpha);
        const diry = Math.sin(alpha);

        for (let i = 0; i <= steps; i++) {
          const v = (i / steps) * Math.PI * 2;
          const vNext = v + (2 * Math.PI) / steps;

          const height = Math.sin(v);
          const xyAmount = Math.cos(v);
          const heightNext = Math.sin(vNext);
          const xyAmountNext = Math.cos(vNext);

          positions.push(dirx * xyAmount, diry * xyAmount, height);
          positions.push(dirx * xyAmountNext, diry * xyAmountNext, heightNext);
        }
      }

      for (let vSegment = 0; vSegment <= vSegments + 1; vSegment++) {
        const uh = vSegment / (vSegments + 1);
        const uhMapped = (1 - uh) * (-Math.PI / 2) + uh * (Math.PI / 2);
        const height = Math.sin(uhMapped);

        for (let i = 0; i <= steps; i++) {
          const u = (i / steps) * Math.PI * 2;
          const uNext = u + (2 * Math.PI) / steps;

          const dirx = Math.cos(u);
          const diry = Math.sin(u);
          const dirxNext = Math.cos(uNext);
          const diryNext = Math.sin(uNext);

          const xyAmount = Math.sqrt(1 - height * height);

          positions.push(dirx * xyAmount, diry * xyAmount, height);
          positions.push(dirxNext * xyAmount, diryNext * xyAmount, height);
        }
      }

      frameGeometry.setAttribute(
        'position',
        new THREE.BufferAttribute(new Float32Array(positions), 3),
      );
    }

    this.frame = new THREE.LineSegments(
      frameGeometry,
      new THREE.LineBasicMaterial({ color: 0x000000 }),
    );
    this.add(this.frame);

    this.update();
  }

  update(): void {
    this.boundingBox = this.sphere.geometry.boundingBox!;
    this.boundingSphere = this.boundingBox.getBoundingSphere(new THREE.Sphere());
  }

  raycast(raycaster: THREE.Raycaster, intersects: THREE.Intersection[]): void {
    const is: THREE.Intersection[] = [];
    this.sphere.raycast(raycaster, is);

    if (is.length > 0) {
      const I = is[0];
      intersects.push({
        distance: I.distance,
        object: this,
        point: I.point.clone(),
      } as any);
    }
  }

  // see https://en.wikipedia.org/wiki/Ellipsoid#Volume
  getVolume(): number {
    return (4 / 3) * Math.PI * this.scale.x * this.scale.y * this.scale.z;
  }
}
